export interface AmlBar {
  category: string;
  label: string;
  share: number;
}

export interface AmlRiskRow {
  category: string;
  label: string;
  level: string;
  share?: number;
}

export interface AmlReportView {
  entityName?: string;
  entityCategory?: string;
  bars: AmlBar[];
  risks: AmlRiskRow[];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function humanize(category: string) {
  return category.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function shareOf(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(100, n <= 1 ? n * 100 : n);
}

function rows(value: unknown): AmlBar[] {
  if (!Array.isArray(value)) return [];
  const out: AmlBar[] = [];
  for (const raw of value) {
    const item = asRecord(raw);
    if (!item) continue;
    const category = String(item.entity_category ?? item.entityCategory ?? item.category ?? 'other');
    const share = shareOf(
      item.value_share ?? item.valueShare ?? item.share_percent ?? item.sharePercent ?? item.percentage,
    );
    if (share <= 0) continue;
    out.push({
      category,
      label: String(item.label ?? humanize(category)),
      share,
    });
  }
  return out.sort((a, b) => b.share - a.share);
}

export function parseAmlReport(exposure: Record<string, unknown> | null | undefined): AmlReportView {
  const root = asRecord(exposure);
  if (!root) return { bars: [], risks: [] };
  const sources = rows(root.sourceOfFunds ?? root.source_of_funds);
  const connections = rows(root.connections ?? root.exposure);
  const bars = (sources.length ? sources : connections).slice(0, 6);
  const risks: AmlRiskRow[] = [];
  if (Array.isArray(root.risks)) {
    for (const raw of root.risks) {
      const item = asRecord(raw);
      if (!item) continue;
      const category = String(item.entity_category ?? item.entityCategory ?? item.category ?? 'other');
      const share = shareOf(item.value_share ?? item.valueShare ?? item.share_percent ?? item.percentage);
      risks.push({
        category,
        label: String(item.label ?? humanize(category)),
        level: String(item.level ?? item.risk_level ?? item.riskLevel ?? ''),
        ...(share > 0 ? { share } : {}),
      });
    }
  }
  const entityName = root.entityName ?? root.entity_name;
  const entityCategory = root.entityCategory ?? root.entity_category;
  return {
    ...(typeof entityName === 'string' && entityName ? { entityName } : {}),
    ...(typeof entityCategory === 'string' && entityCategory ? { entityCategory: humanize(entityCategory) } : {}),
    bars,
    risks: risks.slice(0, 6),
  };
}

export function categoryColor(category: string): string {
  const lower = category.toLowerCase();
  if (['sanction', 'darknet', 'stolen', 'ransomware', 'scam', 'terror', 'child'].some((d) => lower.includes(d))) {
    return '#F05252';
  }
  if (['mixer', 'privacy', 'gambling', 'high_risk', 'exchange'].some((w) => lower.includes(w))) {
    return '#F5A623';
  }
  return '#22B573';
}

export function riskColor(level: number | null): string {
  if (level == null) return '#85858D';
  if (level >= 0.5) return '#F05252';
  if (level >= 0.25) return '#F5A623';
  return '#22B573';
}

export function shortTarget(value: string): string {
  const text = value.trim();
  if (text.length <= 16) return text;
  return `${text.slice(0, 8)}…${text.slice(-6)}`;
}
